import type { SystemMapRouteGeometry, SystemMapStopsResponse } from './api';

export const ALL_STATUSES = ['valid', 'needs_review', 'orphaned', 'other'] as const;
export type StopStatusFilter = (typeof ALL_STATUSES)[number];

const STATUS_LABELS: Record<StopStatusFilter, string> = {
  valid: 'Válida',
  needs_review: 'Requiere revisión',
  orphaned: 'Huérfana',
  other: 'Otro/desconocido',
};

function LineFilter({
  routes,
  selectedLineId,
  onSelectLineId,
}: {
  routes: SystemMapRouteGeometry[];
  selectedLineId: string | null;
  onSelectLineId: (id: string | null) => void;
}): JSX.Element {
  return (
    <div className="smap-panel-block">
      <p className="smap-panel-block-title">Resaltar línea</p>
      <select
        className="smap-select"
        value={selectedLineId ?? ''}
        onChange={e => onSelectLineId(e.target.value || null)}
      >
        <option value="">Todas</option>
        {routes.map(r => (
          <option key={r.analysisId} value={r.analysisId}>
            {r.label}
            {r.routeMode === 'CIRCUIT' ? ' (circuito)' : ''}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Toggle discreto — carga la red urbana perezosamente (el padre solo pide los ~18.900 edges la primera vez que se activa). */
function UrbanNetworkToggle({
  enabled,
  loading,
  onToggle,
}: {
  enabled: boolean;
  loading: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <div className="smap-panel-block">
      <label className="smap-status-check">
        <input type="checkbox" checked={enabled} onChange={onToggle} />
        Red urbana
        {loading && <span className="smap-muted-inline"> (cargando…)</span>}
      </label>
    </div>
  );
}

/** Arranca la cola de revisión secuencial de `needs_review` — deshabilitado mientras ya está activa (se maneja desde la barra inferior) o si no hay pendientes. */
function ReviewStartButton({
  reviewActive,
  needsReviewCount,
  onStart,
}: {
  reviewActive: boolean;
  needsReviewCount: number;
  onStart: () => void;
}): JSX.Element {
  return (
    <div className="smap-panel-block">
      <button type="button" className="smap-reset-btn" onClick={onStart} disabled={reviewActive || needsReviewCount === 0}>
        Revisar pendientes ({needsReviewCount})
      </button>
    </div>
  );
}

/** Arma/desarma el flujo "Nueva parada" — mientras está armado, el próximo click en el mapa (fuera de una parada o un edge existente) coloca el marker provisional. */
function NewStopToggle({ armed, onToggle }: { armed: boolean; onToggle: () => void }): JSX.Element {
  return (
    <div className="smap-panel-block">
      <button
        type="button"
        className={`smap-reset-btn smap-new-stop-toggle${armed ? ' smap-new-stop-toggle--armed' : ''}`}
        onClick={onToggle}
      >
        {armed ? 'Cancelar nueva parada (clic en el mapa)' : '+ Nueva parada'}
      </button>
    </div>
  );
}

function StatusFilter({
  activeStatuses,
  onToggleStatus,
}: {
  activeStatuses: Set<StopStatusFilter>;
  onToggleStatus: (status: StopStatusFilter) => void;
}): JSX.Element {
  return (
    <div className="smap-panel-block">
      <p className="smap-panel-block-title">Estado QA</p>
      <div className="smap-status-checks">
        {ALL_STATUSES.map(status => (
          <label key={status} className="smap-status-check">
            <input
              type="checkbox"
              checked={activeStatuses.has(status)}
              onChange={() => onToggleStatus(status)}
            />
            <span className={`smap-status-dot smap-status-dot--${status}`} />
            {STATUS_LABELS[status]}
          </label>
        ))}
      </div>
    </div>
  );
}

function Counters({
  visibleCount,
  counts,
}: {
  visibleCount: number;
  counts: SystemMapStopsResponse['counts'];
}): JSX.Element {
  return (
    <div className="smap-panel-block">
      <p className="smap-panel-block-title">Paradas</p>
      <div className="smap-counts">
        <div className="smap-counts-row">
          <span>Visibles / total</span>
          <strong>
            {visibleCount} / {counts.total}
          </strong>
        </div>
        <div className="smap-counts-row">
          <span>Válidas</span>
          <strong>{counts.valid}</strong>
        </div>
        <div className="smap-counts-row">
          <span>Requieren revisión</span>
          <strong>{counts.needsReview}</strong>
        </div>
        <div className="smap-counts-row">
          <span>Huérfanas</span>
          <strong>{counts.orphaned}</strong>
        </div>
        {counts.other > 0 && (
          <div className="smap-counts-row">
            <span>Otro/desconocido</span>
            <strong>{counts.other}</strong>
          </div>
        )}
      </div>
    </div>
  );
}

export function SystemMapControls({
  routes,
  selectedLineId,
  onSelectLineId,
  activeStatuses,
  onToggleStatus,
  visibleCount,
  counts,
  onReset,
  urbanNetworkEnabled,
  urbanNetworkLoading,
  onToggleUrbanNetwork,
  reviewActive,
  onStartReview,
  newStopArmed,
  onToggleNewStop,
}: {
  routes: SystemMapRouteGeometry[];
  selectedLineId: string | null;
  onSelectLineId: (id: string | null) => void;
  activeStatuses: Set<StopStatusFilter>;
  onToggleStatus: (status: StopStatusFilter) => void;
  visibleCount: number;
  counts: SystemMapStopsResponse['counts'];
  onReset: () => void;
  urbanNetworkEnabled: boolean;
  urbanNetworkLoading: boolean;
  onToggleUrbanNetwork: () => void;
  reviewActive: boolean;
  onStartReview: () => void;
  newStopArmed: boolean;
  onToggleNewStop: () => void;
}): JSX.Element {
  return (
    <div className="smap-controls">
      <LineFilter routes={routes} selectedLineId={selectedLineId} onSelectLineId={onSelectLineId} />
      <UrbanNetworkToggle
        enabled={urbanNetworkEnabled}
        loading={urbanNetworkLoading}
        onToggle={onToggleUrbanNetwork}
      />
      <StatusFilter activeStatuses={activeStatuses} onToggleStatus={onToggleStatus} />
      <Counters visibleCount={visibleCount} counts={counts} />
      <ReviewStartButton reviewActive={reviewActive} needsReviewCount={counts.needsReview} onStart={onStartReview} />
      <NewStopToggle armed={newStopArmed} onToggle={onToggleNewStop} />
      <div className="smap-panel-block">
        <button type="button" className="smap-reset-btn" onClick={onReset}>
          Reiniciar filtros
        </button>
      </div>
    </div>
  );
}
